import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import { reviewsCollectionPageLoaded } from '@wix/bi-logger-reviews-data/v2';
import { createSettingsEventHandler } from '~commons/settings/create-settings-event-handler';
import { SET_CTA_EFFECT, SettingsEvent } from './common/settings/events';
import { ThankYouWidgetProps, ViewType } from '~/components/ThankYouWidget/Widget/types';
import { ThankYouView } from '~commons/types';
import { buildBiLogger } from '~commons/bi/bi-logger';
import { createInstanceManager } from '~commons/instance/get-instance';
import { REVIEWS_APP_ID } from '~/app-ids';
import { unreachable } from '~/ts-utils';

const createController: CreateControllerFn = async (controllerParams) => {
  // For more info about controller structure,
  // check the docs: https://bo.wix.com/pages/yoshi/docs/editor-flow/structure-api/component/ooi-component#controller
  const settingsEventHandler = createSettingsEventHandler<SettingsEvent>(
    controllerParams.controllerConfig.config.publicData.COMPONENT ?? {},
  );
  return {
    async pageReady() {
      const flowAPI = controllerParams.flowAPI;
      const { setProps, wixCodeApi } = flowAPI.controllerConfig;

      const { view, ctaLink } = wixCodeApi.location.query;

      const defaultState: ThankYouWidgetProps & { fitToContentHeight: boolean } = {
        ctaState: 'Default',
        ctaLink: decodeURIComponent(ctaLink),
        view: parseViewQueryParam(view),
        isEditor: flowAPI.environment.isEditor,
        isPreview: flowAPI.environment.isPreview,
        // https://wix.slack.com/archives/CAKBA7TDH/p1646144638497679?thread_ts=1643623637.998229&cid=CAKBA7TDH
        fitToContentHeight: true,
      };

      setProps(defaultState);

      const instanceManager = createInstanceManager(wixCodeApi, REVIEWS_APP_ID);
      const logger = buildBiLogger(instanceManager, flowAPI);
      logger.report(
        reviewsCollectionPageLoaded({
          origin: 'collection_page',
          message: mapViewToBiMessage(defaultState.view),
        }),
      );

      settingsEventHandler.on(SET_CTA_EFFECT, (payload) => {
        setProps({ ...defaultState, ctaState: payload });
      });

      settingsEventHandler.onSettingsClose(() => setProps(defaultState));
    },
    updateConfig(_, { publicData }) {
      settingsEventHandler.updateData(publicData.COMPONENT ?? {});
    },
  };
};

const parseViewQueryParam = (view: string): ViewType => {
  switch (view as ThankYouView) {
    case 'review-exists':
      return 'REVIEW_ALREADY_EXISTS';
    case 'entity-not-found':
      return 'ENTITY_NOT_FOUND';
    case 'review-pending':
      return 'REVIEW_PENDING';
    case 'review-pending-deeplink':
      return 'REVIEW_PENDING_DEEPLINK';
    case 'default':
    default:
      return 'DEFAULT';
  }
};

const mapViewToBiMessage = (view: ViewType) => {
  switch (view) {
    case 'ENTITY_NOT_FOUND':
      return 'error';
    case 'REVIEW_ALREADY_EXISTS':
      return 'already_left_review';
    case 'REVIEW_PENDING_DEEPLINK':
    case 'REVIEW_PENDING':
      return 'review_pending';
    case 'DEFAULT':
      return 'review_visible';
    default:
      throw unreachable(view);
  }
};

export default createController;
